import { formatDateRange } from "@/lib/dateUtils";
import React, { useState, useEffect, useCallback } from "react";
import { useAuth } from "@/contexts/AuthContext";
import api from "@/lib/api";
import RetailRankingSection from "@/components/RetailRankingSection";
import BenefitsSection from "@/components/BenefitsSection";
import RecapsListSection from "@/components/RecapsListSection";
import "./EarningsPage.css";
import { CuteLoader } from "@/components/CuteLoader";

const money = (n) => (Number(n) || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

export default function EarningsPage() {
  const { user } = useAuth();
  const [periods, setPeriods] = useState([]);
  const [selectedPeriod, setSelectedPeriod] = useState(null);
  const [periodData, setPeriodData] = useState(null);
  const [ytdData, setYtdData] = useState(null);
  const [yoy, setYoy] = useState(null);
  const [history2025, setHistory2025] = useState(null);
  const [yearView, setYearView] = useState("2026");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [taxOpen, setTaxOpen] = useState(false);

  const loadData = useCallback(async () => {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 10000);
    
    try {
      const periodsRes = await api.get("/employee/periods", { signal: controller.signal });
      const sorted = periodsRes.data.sort((a, b) => new Date(b.end_date) - new Date(a.end_date));
      setPeriods(sorted);
      
      if (sorted.length > 0) {
        setSelectedPeriod(sorted[0].period_id);
      }
      
      const [ytdRes, yoyRes, histRes] = await Promise.all([
        api.get("/employee/earnings/ytd", { signal: controller.signal }),
        api.get("/employee/earnings/yoy", { signal: controller.signal }),
        api.get("/employee/pay-history/2025", { signal: controller.signal }),
      ]);
      setYtdData(ytdRes.data);
      setYoy(yoyRes.data);
      setHistory2025(histRes.data);
      
      setError(null);
    } catch (error) {
      if (error.name === 'AbortError') {
        setError('Request timed out');
      } else {
        setError('Could not load earnings');
      }
    } finally {
      clearTimeout(timeoutId);
      setLoading(false);
    }
  }, []);

  const loadPeriodData = useCallback(async (periodId) => {
    if (!periodId) return;
    
    try {
      const res = await api.get(`/employee/earnings/period/${periodId}`);
      setPeriodData(res.data);
    } catch (error) {
      console.error("Period data error:", error);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  useEffect(() => {
    if (selectedPeriod) {
      loadPeriodData(selectedPeriod);
    }
  }, [selectedPeriod, loadPeriodData]);

  if (loading) {
    return <CuteLoader />;
  }

  if (error) {
    return (
      <div className="earnings-page-error">
        <div className="error-message">{error}</div>
        <button onClick={() => { setLoading(true); setError(null); loadData(); }} className="retry-button">
          Retry
        </button>
      </div>
    );
  }

  const selectedPeriodObj = periods.find(p => p.period_id === selectedPeriod);
  const is2025 = selectedPeriodObj?.start_date?.startsWith("2025");

  return (
    <div className="earnings-page">
      <div className="earnings-header">
        <h1>Earnings</h1>
        <p className="earnings-subtitle">Your compensation breakdown</p>
      </div>

      {yoy && yoy.has_comparison && (
        <div className="earnings-body" style={{ paddingBottom: 0 }}>
          <div className="ytd-card" data-testid="earnings-yoy" style={{ background: "linear-gradient(168deg,#231E1A,#3A2A1E)", color: "#F3EEE7" }}>
            <div className="ytd-header" style={{ color: "#B0905E" }}>This year so far vs last year</div>
            <div className="ytd-row">
              <span className="ytd-label" style={{ color: "rgba(243,238,231,0.7)" }}>{new Date().getFullYear()} so far</span>
              <span className="ytd-amount" style={{ color: "#F3EEE7" }}>${money(yoy.this_year)}</span>
            </div>
            <div className="ytd-row">
              <span className="ytd-label" style={{ color: "rgba(243,238,231,0.7)" }}>Last year, same point</span>
              <span className="ytd-amount" style={{ color: "#F3EEE7" }}>${money(yoy.last_year)}</span>
            </div>
            <div className="ytd-row ytd-total" style={{ borderColor: "rgba(243,238,231,0.2)" }}>
              <span className="ytd-label" style={{ color: "rgba(243,238,231,0.7)" }}>{yoy.delta >= 0 ? "Ahead by" : "Behind by"}</span>
              <span className="ytd-amount" style={{ color: "#B0905E" }}>
                ${money(Math.abs(yoy.delta))}{yoy.pct != null ? ` (${yoy.delta >= 0 ? "+" : "−"}${Math.abs(yoy.pct)}%)` : ""}
              </span>
            </div>
          </div>
        </div>
      )}

      {history2025 && history2025.has_data && (
        <div className="earnings-body" style={{ paddingTop: 12, paddingBottom: 0 }}>
          <div className="period-selector-card" style={{ display: "flex", gap: 8, padding: 6 }} data-testid="earnings-year-toggle">
            <button onClick={() => setYearView("2026")} data-testid="year-tab-2026"
              style={{ flex: 1, padding: "10px", borderRadius: 8, border: "none", cursor: "pointer",
                background: yearView === "2026" ? "#9C6B4A" : "transparent", color: yearView === "2026" ? "#fff" : "#9C6B4A", fontWeight: 600 }}>
              {new Date().getFullYear()}
            </button>
            <button onClick={() => setYearView("2025")} data-testid="year-tab-2025"
              style={{ flex: 1, padding: "10px", borderRadius: 8, border: "none", cursor: "pointer",
                background: yearView === "2025" ? "#9C6B4A" : "transparent", color: yearView === "2025" ? "#fff" : "#9C6B4A", fontWeight: 600 }}>
              2025
            </button>
          </div>
        </div>
      )}

      <div className="earnings-body">
        {yearView === "2025" && history2025 && history2025.has_data ? (
          <div className="earnings-breakdown-card" data-testid="earnings-2025-view">
            <div className="breakdown-header">2025 · Paid amounts</div>
            {history2025.is_1099 && (
              <div className="breakdown-meta" style={{ marginBottom: 8 }}>
                <span>{history2025.compensation_label}</span>
              </div>
            )}
            {history2025.checks.map((c) => (
              <div className="breakdown-row" key={c.check_date} data-testid={`check-${c.check_date}`}>
                <span className="breakdown-label">{formatDateRange(c.check_date, c.check_date).split(" – ")[0]}</span>
                <span className="breakdown-amount">${money(c.total)}</span>
              </div>
            ))}
            <div className="breakdown-row breakdown-total">
              <span className="breakdown-label">2025 Total</span>
              <span className="breakdown-amount">${money(history2025.totals.total)}</span>
            </div>
            <div className="breakdown-meta" style={{ flexWrap: "wrap", gap: 12 }}>
              {history2025.totals.hourly > 0 && <span>Hourly ${money(history2025.totals.hourly)}</span>}
              {history2025.totals.commission > 0 && <span>{history2025.commission_display_label || "Commission"} ${money(history2025.totals.commission)}</span>}
              {history2025.totals.other_earnings > 0 && <span>Other earnings ${money(history2025.totals.other_earnings)}</span>}
              {history2025.totals.compensation > 0 && <span>{history2025.compensation_label} ${money(history2025.totals.compensation)}</span>}
              {history2025.totals.reg_hours > 0 && <span>{history2025.totals.reg_hours.toLocaleString()} hours</span>}
            </div>
          </div>
        ) : (
        <>
        <div className="period-selector-card">
          <label className="period-selector-label">Pay Period</label>
          <select
            value={selectedPeriod || ""}
            onChange={(e) => setSelectedPeriod(e.target.value)}
            className="period-selector"
          >
            {periods.map((p) => (
              <option key={p.period_id} value={p.period_id}>
                {formatDateRange(p.start_date, p.end_date)}
              </option>
            ))}
          </select>
        </div>

        {periodData && (
          <div className="earnings-breakdown-card">
            <div className="breakdown-header">{periodData.after_departure ? periodData.period_label : "This Period"}</div>
            
            {!periodData.after_departure && periodData.hourly !== null && (
            <div className="breakdown-row">
              <span className="breakdown-label">Hourly Wages</span>
              <span className="breakdown-amount">${money(periodData.hourly)}</span>
            </div>
            )}
            {periodData.commission !== null && periodData.commission !== undefined && !is2025 && (!periodData.after_departure || periodData.commission > 0) && (
              <div className="breakdown-row">
                <span className="breakdown-label">{periodData.commission_label || "Service Commission"}</span>
                <span className="breakdown-amount">${money(periodData.commission)}</span>
              </div>
            )}
            
            {is2025 && periodData.commission > 0 && (
              <div className="breakdown-row">
                <span className="breakdown-label">Commission (Paid)</span>
                <span className="breakdown-amount">${money(periodData.commission)}</span>
              </div>
            )}
            
            {periodData.retail_commission > 0 && (
              <div className="breakdown-row" data-testid="period-retail-commission-row">
                <span className="breakdown-label">Retail Commission</span>
                <span className="breakdown-amount">${money(periodData.retail_commission)}</span>
              </div>
            )}

            {periodData.tips > 0 && (
              <div className="breakdown-row">
                <span className="breakdown-label">Tips</span>
                <span className="breakdown-amount">${money(periodData.tips)}</span>
              </div>
            )}
            
            <div className="breakdown-row breakdown-total">
              <span className="breakdown-label">Total Earnings</span>
              <span className="breakdown-amount">${money(periodData.total)}</span>
            </div>

            {/* Taxes & take-home — only when verified ADP deduction data exists */}
            {periodData.take_home && periodData.take_home.variant === "employee" && (
              <div data-testid="period-take-home">
                <button
                  type="button"
                  data-testid="period-taxes-toggle"
                  onClick={() => setTaxOpen(!taxOpen)}
                  className="breakdown-row"
                  style={{ width: "100%", background: "none", border: "none", borderTop: "1px solid #EAE1D4",
                           textAlign: "left", cursor: (periodData.take_home.withholdings || []).length ? "pointer" : "default",
                           font: "inherit", minHeight: 44, padding: "12px 0" }}>
                  <span className="breakdown-label">
                    Taxes withheld{(periodData.take_home.withholdings || []).length ? (taxOpen ? " ▾" : " ▸") : ""}
                  </span>
                  <span className="breakdown-amount" data-testid="period-taxes-total">−${money(periodData.take_home.total_withheld)}</span>
                </button>
                {taxOpen && (periodData.take_home.withholdings || []).map((w, i) => (
                  <div className="breakdown-row" key={i} data-testid={`period-withholding-${i}`}
                       style={{ paddingLeft: 16 }}>
                    <span className="breakdown-label" style={{ color: "#8A7355" }}>{w.label}</span>
                    <span className="breakdown-amount" style={{ color: "#8A7355" }}>−${money(w.amount)}</span>
                  </div>
                ))}
                <div className="breakdown-row breakdown-total" data-testid="period-net-row">
                  <span className="breakdown-label">Take-home after taxes</span>
                  <span className="breakdown-amount">${money(periodData.take_home.net_pay)}</span>
                </div>
              </div>
            )}
            {periodData.take_home && periodData.take_home.variant === "contractor" && (
              <div className="breakdown-meta" data-testid="period-contractor-note"
                   style={{ marginTop: 4, fontStyle: "italic", color: "#8A7355", lineHeight: 1.5 }}>
                {periodData.take_home.note}
              </div>
            )}

            <div className="breakdown-meta">
              {periodData.hours > 0 && <span>{periodData.hours} hours</span>}
              {periodData.status === "finalized" && <span className="finalized-badge">Finalized</span>}
            </div>
          </div>
        )}

        {ytdData && (
          <div className="ytd-card">
            <div className="ytd-header">Year to Date {new Date().getFullYear()}</div>
            
            <div className="ytd-row">
              <span className="ytd-label">Total Hourly</span>
              <span className="ytd-amount">${money(ytdData.total_hourly)}</span>
            </div>
            
            {ytdData.total_commission > 0 && (
              <div className="ytd-row">
                <span className="ytd-label">Total {history2025?.commission_display_label || "Commission"}</span>
                <span className="ytd-amount">${money(ytdData.total_commission)}</span>
              </div>
            )}
            
            {ytdData.total_tips > 0 && (
              <div className="ytd-row">
                <span className="ytd-label">Total Tips</span>
                <span className="ytd-amount">${money(ytdData.total_tips)}</span>
              </div>
            )}
            
            <div className="ytd-row ytd-total">
              <span className="ytd-label">Year to Date</span>
              <span className="ytd-amount">${money(ytdData.ytd_total)}</span>
            </div>

            {ytdData.tax && ytdData.tax.variant === "employee" && (
              <div data-testid="ytd-tax">
                <div className="ytd-row" style={{ marginTop: 4 }}>
                  <span className="ytd-label">Taxes withheld YTD</span>
                  <span className="ytd-amount" data-testid="ytd-taxes-withheld">−${money(ytdData.tax.total_withheld)}</span>
                </div>
                <div className="ytd-row">
                  <span className="ytd-label">Take-home YTD</span>
                  <span className="ytd-amount" data-testid="ytd-take-home">${money(ytdData.tax.take_home)}</span>
                </div>
                {ytdData.tax.partial_coverage && (
                  <div className="ytd-row" data-testid="ytd-partial-coverage" style={{ borderTop: "none", paddingTop: 2 }}>
                    <span className="ytd-label" style={{ fontSize: 12, fontStyle: "italic", opacity: 0.75 }}>
                      Across {ytdData.tax.covered_paydays} of {ytdData.tax.total_paydays} paydays with tax data
                    </span>
                  </div>
                )}
              </div>
            )}
            {ytdData.tax && ytdData.tax.variant === "contractor" && (
              <div data-testid="ytd-contractor-note" style={{ marginTop: 8, fontSize: 13, fontStyle: "italic", opacity: 0.8 }}>
                {ytdData.tax.note}
              </div>
            )}
          </div>
        )}
        </>
        )}
      </div>

      <RetailRankingSection />
      <BenefitsSection />
      <RecapsListSection />
    </div>
  );
}
